import { useEffect, useState, useCallback } from "react";
import {
  listContentPieces,
  getContentPiece,
  deleteContentPiece,
} from "@/lib/ai";
import { PageHeader } from "@/components/PageHeader";
import { Tag } from "@/components/Tag";
import { fmtDate } from "@/lib/format";
import { Trash2, ArrowRight, X } from "lucide-react";
import { MarkdownResult, BatchResult } from "@/pages/CreateContentPage";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TYPE_LABEL = {
  batch: "Weekly batch",
  blog_basic: "Basic blog",
  blog_aeo: "AEO blog",
  live_video: "Live video",
};

export default function ContentLibraryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await listContentPieces();
    setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPiece = async (id) => {
    try {
      const piece = await getContentPiece(id);
      setOpenItem(piece);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const removePiece = async (id) => {
    if (!window.confirm("Delete this content piece?")) return;
    try {
      await deleteContentPiece(id);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="content-library-page">
      <PageHeader
        eyebrow="Library"
        title="Content library"
        subtitle="Every batch, blog, and video script you've saved. Pull these up when batching content or repurposing across platforms."
        testId="content-library-header"
      />

      <div className="mb-3 text-xs text-[#141B4D]/60" data-testid="library-count">
        {loading ? "Loading…" : `${items.length} content pieces`}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-[#141B4D]">
              <th className="py-3 pr-4 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Type
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[40%]">
                Title
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[35%]">
                Belief
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Created
              </th>
              <th className="py-3 pl-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[1%]"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                data-testid={`library-row-${item.id}`}
                className="border-b border-[#141B4D]/15 hover:bg-[#141B4D]/5"
              >
                <td className="py-4 pr-4 align-top">
                  <Tag>{TYPE_LABEL[item.content_type] || item.content_type}</Tag>
                </td>
                <td className="py-4 px-3 align-top">
                  <button
                    onClick={() => openPiece(item.id)}
                    data-testid={`library-open-${item.id}`}
                    className="text-sm text-[#141B4D] font-medium text-left hover:underline decoration-[#FFD100] decoration-2 underline-offset-4"
                  >
                    {item.title || "(untitled)"}
                  </button>
                </td>
                <td className="py-4 px-3 align-top text-xs text-[#141B4D]/70 line-clamp-2">
                  {item.belief_text || "—"}
                </td>
                <td className="py-4 px-3 align-top text-xs text-[#141B4D]/60 whitespace-nowrap">
                  {fmtDate(item.created_at)}
                </td>
                <td className="py-4 pl-3 align-top whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openPiece(item.id)}
                      data-testid={`library-view-${item.id}`}
                      className="p-1.5 text-[#141B4D]/60 hover:text-[#141B4D] hover:bg-[#141B4D]/5 rounded-sm"
                      title="Open"
                    >
                      <ArrowRight size={14} />
                    </button>
                    <button
                      onClick={() => removePiece(item.id)}
                      data-testid={`library-delete-${item.id}`}
                      className="p-1.5 text-[#141B4D]/60 hover:text-rose-600 hover:bg-rose-50 rounded-sm"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="py-12 text-center text-sm text-[#141B4D]/50">
                  No saved content yet. Head to Create Content and generate
                  your first piece.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!openItem} onOpenChange={(v) => !v && setOpenItem(null)}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm"
          data-testid="library-piece-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-[#141B4D] text-xl font-bold tracking-tight">
              {openItem?.title || "Content piece"}
            </DialogTitle>
          </DialogHeader>
          {openItem && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Tag>{TYPE_LABEL[openItem.content_type]}</Tag>
                <span className="text-xs text-[#141B4D]/60">
                  {fmtDate(openItem.created_at)}
                </span>
              </div>
              {openItem.belief_text && (
                <div className="mb-4 pb-4 border-b border-[#141B4D]/15">
                  <div className="text-xs uppercase tracking-wider text-[#141B4D]/60 mb-1">
                    Belief
                  </div>
                  <div className="text-sm text-[#141B4D]">
                    {openItem.belief_text}
                  </div>
                </div>
              )}
              {openItem.content_type === "batch" ? (
                <BatchResult batch={openItem.body} />
              ) : (
                <MarkdownResult markdown={openItem.body?.markdown || ""} />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
